import { Stack } from '@mantine/core';
import { schemaResolver, useForm } from '@mantine/form';
import { IconCircleHalf2, IconFile, IconFiles, IconPalette } from '@tabler/icons-react';
import { Panel } from 'components/Panel';
import { SaveButton } from 'components/SaveButton';
import { i18nKeys, useTranslation } from 'locales';
import { useEffect } from 'react';
import type { z } from 'zod/v4';

import { type CardOption, OptionCardGroup } from '../../components';
import {
  PostalSendingOptionsSchema,
  type UpdatePostalSendingOptionsVariables,
  useLoadPostalSendingOptions,
  useUpdatePostalSendingOptions,
} from '../api';

type PostalSendingFormValues = z.infer<typeof PostalSendingOptionsSchema>;

const INITIAL_VALUES: PostalSendingFormValues = {
  printColor: 'BW',
  printMode: '1SIDE',
};

// Both fields are closed two-value sets in the Postgreen API, so the options are
// spelled out here rather than fetched from the shared constants endpoint.
const PRINT_COLOR_OPTIONS: CardOption<PostalSendingFormValues['printColor']>[] = [
  { value: 'BW', labelKey: i18nKeys.FORM.PREFERENCES.PRINT_COLORS.BW, Icon: IconCircleHalf2 },
  { value: 'COLOR', labelKey: i18nKeys.FORM.PREFERENCES.PRINT_COLORS.COLOR, Icon: IconPalette },
];

const PRINT_MODE_OPTIONS: CardOption<PostalSendingFormValues['printMode']>[] = [
  { value: '1SIDE', labelKey: i18nKeys.FORM.PREFERENCES.PRINT_MODES.ONE_SIDE, Icon: IconFile },
  { value: '2SIDE', labelKey: i18nKeys.FORM.PREFERENCES.PRINT_MODES.TWO_SIDE, Icon: IconFiles },
];

export const PostalSendingCard = () => {
  const { t } = useTranslation();

  const { postalSendingOptions } = useLoadPostalSendingOptions();
  const { updatePostalSendingOptions, isUpdatePostalSendingOptionsLoading } =
    useUpdatePostalSendingOptions();

  const form = useForm<PostalSendingFormValues>({
    initialValues: INITIAL_VALUES,
    validate: schemaResolver(PostalSendingOptionsSchema, { sync: true }),
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: sync server-loaded options into the form only when the fetched data changes, not on every render (the form object identity is unstable)
  useEffect(() => {
    if (!postalSendingOptions) return;

    form.setInitialValues(postalSendingOptions);
    form.setValues(postalSendingOptions);
  }, [postalSendingOptions]);

  const handleSave = () => {
    const values = form.getValues();
    const payload: UpdatePostalSendingOptionsVariables = {};
    if (form.isDirty('printColor')) payload.printColor = values.printColor;
    if (form.isDirty('printMode')) payload.printMode = values.printMode;

    updatePostalSendingOptions(payload, { onSuccess: () => form.resetDirty() });
  };

  const { printColor, printMode } = form.getValues();

  return (
    <Panel
      title={t(i18nKeys.FORM.PREFERENCES.PREFERENCES_POSTAL_SEND)}
      withBorder
      fullHeight
      pinFooter
      footer={
        <SaveButton
          onClick={handleSave}
          disabled={!form.isDirty()}
          loading={isUpdatePostalSendingOptionsLoading}
          hideIcon
        />
      }
    >
      <Stack my="md">
        <OptionCardGroup
          label={t(i18nKeys.FORM.PREFERENCES.COLOR)}
          value={printColor}
          options={PRINT_COLOR_OPTIONS}
          onChange={next => form.setFieldValue('printColor', next)}
        />
        <OptionCardGroup
          label={t(i18nKeys.FORM.PREFERENCES.PRINTING)}
          value={printMode}
          options={PRINT_MODE_OPTIONS}
          onChange={next => form.setFieldValue('printMode', next)}
        />
      </Stack>
    </Panel>
  );
};
